/**
 * EL HISTÓRICO DE AJUSTES, COMPARTIDO (doc 19-08, bloque 09).
 *
 * La misma tabla vive en dos sitios según el plan: en «Mis macros» para el que se los
 * calcula él, y en Seguimiento → Evolución para el que se los lleva un entrenador («al
 * lado de su peso, sus medidas y sus fotos. Que es donde se ve que estás trabajando»).
 * Un solo componente para que nunca cuenten historias distintas.
 *
 * También sale de aquí la frase del último ajuste de la cabecera del doc:
 * «Último ajuste: −20 g de hidratos en entreno, −10 g de grasa en descanso».
 *
 * Desde el doc de Jesús del 2-09 (duda 37) se llama «Tus macros, ajuste a ajuste» y enseña
 * los cuatro últimos de entrada; el resto, detrás de «Ver los N ajustes».
 */
import React from 'react';
import TablaDeslizable from './ui/TablaDeslizable';
import { Link } from 'react-router-dom';
import { MACRO } from '../pages/ClientDashboard';
import { num1 } from '../lib/numeros';
import GraficaDeAjustes from './GraficaDeAjustes';
import { normalizarObjetivo } from '../lib/objetivos';

const CAMPOS = [
    { key: 'proteina', corto: 'P', nombre: 'proteína', color: MACRO.protein },
    { key: 'hidratos', corto: 'H', nombre: 'hidratos', color: MACRO.carbs },
    { key: 'grasa', corto: 'G', nombre: 'grasa', color: MACRO.fat },
];

const numero = (v) => (v === null || v === undefined ? '-' : v);

// LA PASTILLA DEL OBJETIVO (Jesús, 7-09, regla 01·9): «Definición en naranja sólido, volumen
// en naranja perfilado, recomposición en ámbar y mantenimiento en gris».
//
// Él nombra cuatro y la app lleva seis, porque la lista cerrada de objetivos de su documento
// del 2-09 tiene dos más. Los cuatro suyos van con su color exacto; a los otros dos se les da
// el más cercano y se queda anotado: «máxima definición» es una definición, así que va con la
// suya, y «tonificación» se queda en neutro hasta que él le ponga color.
const PASTILLA = {
    perder_grasa: { texto: 'Definición', clase: 'bg-brand text-white' },
    maxima_definicion: { texto: 'Máx. def.', clase: 'bg-brand text-white' },
    ganar_volumen: { texto: 'Volumen', clase: 'border border-brand text-brand' },
    recomposicion: { texto: 'Recomp.', clase: 'bg-amber-500/20 text-amber-600 dark:text-amber-400' },
    mantenimiento: { texto: 'Mantenim.', clase: 'bg-muted text-muted-foreground' },
    tonificacion: { texto: 'Tonific.', clase: 'bg-muted text-muted-foreground' },
};

// DONDE NO HAY OBJETIVO NO SE PINTA NADA. Medido en producción el 7-09: el campo estaba en 52
// de 3.702 filas. Desde hoy se rellena al guardar y lo viejo se resuelve por el ciclo, pero
// para lo que no cae en ningún ciclo un hueco es más honesto que un «mantenimiento» por
// defecto, que es justo el valor que más veces mentiría.
const Objetivo = ({ clave }) => {
    const p = PASTILLA[normalizarObjetivo(clave) || ''];
    if (!p) return null;
    return (
        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${p.clase}`}>
            {p.texto}
        </span>
    );
};

export const fechaCorta = (iso) => {
    if (!iso) return '-';
    return new Date(iso + 'T12:00:00').toLocaleDateString('es-ES', {
        day: 'numeric', month: 'short', year: '2-digit',
    });
};

/**
 * «Último ajuste: −20 g de hidratos en entreno, −10 g de grasa en descanso».
 * Se compara el ajuste vigente con el anterior, bloque a bloque; lo que no se movió no se
 * nombra. Sin anterior (el primer ajuste) no hay frase.
 */
export const ultimoAjusteLegible = (entradas) => {
    if (!entradas || entradas.length < 2) return null;
    const [ahora, antes] = entradas;
    const BLOQUES = [['entreno', 'entreno'], ['peri', 'perientreno'], ['descanso', 'descanso']];
    const partes = [];
    for (const [key, nombre] of BLOQUES) {
        for (const c of CAMPOS) {
            const a = ahora?.[key]?.[c.key];
            const b = antes?.[key]?.[c.key];
            if (a == null || b == null) continue;
            const delta = a - b;
            if (!delta) continue;
            partes.push(`${delta > 0 ? '+' : '−'}${Math.abs(delta)} g de ${c.nombre} en ${nombre}`);
        }
    }
    return partes.length ? partes.join(', ') : null;
};

// EN ROJO LO QUE CAMBIÓ RESPECTO A LA FILA DE ARRIBA, Y CALCULADO (Jesús, 7-09, regla 03·2).
//
// Se comparaba con el campo `cambios`, que se escribe al guardar el ajuste. La promesa del
// rótulo -- «en rojo, lo que cambió» -- era falsa para casi todos: medido en producción el
// 7-09, solo 38 de 3.702 filas lo llevan, porque el campo nació el 05-08 y las importadas de
// Calma no lo tienen. En personas: 8 clientes de 186 veían alguna celda roja.
//
// Comparando aquí con el ajuste anterior no hay que guardar nada, funciona en las 3.702 filas
// y es lo que hace su calculadora antigua: «el rojo es contra la fila anterior, no un dato
// guardado». La fila más vieja no tiene con qué compararse y se queda sin marcar.
//
// OJO: la comparación es contra el array ENTERO, no contra las cuatro que se ven. Si no, la
// cuarta fila de la portada saldría sin marcar por no tener vecina a la vista.
const Celdas = ({ macros, anterior, conGrasa = true }) => {
    const campos = conGrasa ? CAMPOS : CAMPOS.slice(0, 2);
    return campos.map(c => {
        const ahora = macros?.[c.key];
        const antes = anterior?.[c.key];
        const cambio = anterior != null && ahora != null && antes != null && ahora !== antes;
        return (
            <td key={c.key}
                className={`px-1.5 py-2 text-right font-data ${cambio ? 'text-red-600 dark:text-red-400 font-bold' : 'text-foreground/70'}`}>
                {numero(ahora)}
            </td>
        );
    });
};

// EL PESO MÁS BAJO EN VERDE Y EL MÁS ALTO EN ROJO (Jesús, 7-09, reglas 01·10 y 03·3: «tu
// regla de siempre»). Sobre las filas que se están enseñando, que es como está dibujado: en
// la portada marca el mínimo y el máximo de los cuatro últimos ajustes, y en el histórico
// completo, los de todo. Con un solo peso no se marca nada: no hay ni mínimo ni máximo.
const extremosDePeso = (filas) => {
    const pesos = filas.map(h => h.peso).filter(p => typeof p === 'number' && p > 0);
    if (pesos.length < 2) return {};
    const min = Math.min(...pesos);
    const max = Math.max(...pesos);
    return min === max ? {} : { min, max };
};

// Cuántos ajustes se enseñan de entrada; el resto a un clic («Ver los 14 ajustes»).
// Eran doce, y con diez columnas cada uno la tabla se comía la pantalla. Jesús, doc del
// 2-09 (duda 37): «que salgan los cuatro últimos y el resto detrás de Ver los 14 ajustes,
// como ya está». Los cuatro últimos son los que cuentan la historia reciente; el resto
// sigue a un clic.
const AJUSTES_A_LA_VISTA = 4;

/**
 * `completo`: sin recorte ni botón, para la pantalla «Todo tu histórico» (la 03 del doc del
 * 7-09). En la portada se enseñan los cuatro últimos y el resto vive en esa pantalla.
 */
const HistorialDeMacros = ({ entradas, completo = false }) => {
    const filas = entradas || [];
    const visibles = completo ? filas : filas.slice(0, AJUSTES_A_LA_VISTA);
    const extremos = extremosDePeso(visibles);
    // Con una sola entrada no hay escalera que leer.
    if (filas.length < 2) return null;
    return (
        <section className="space-y-2" data-testid="mis-macros-historico">
            {/* «Se llama Histórico de macros y peso, no "cómo han ido cambiando"» (Jesús,
                7-09, regla 01·7). Corrige al nombre que él mismo puso el 2-09, «Tus macros,
                ajuste a ajuste»: manda la decisión más reciente.

                En la pantalla completa no se pinta: allí el título ya es «Todo tu histórico»
                y repetirlo dejaba dos rótulos pegados diciendo lo mismo. */}
            {!completo && <p className="caption">Histórico de macros y peso</p>}
            {/* LA GRÁFICA, ENCIMA DE LA TABLA (regla 01·7 y pantalla 03). Va con el bloque
                entero porque el rótulo ya promete las dos cosas, «macros y peso», y porque las
                dos salen de las mismas filas: no pueden contarse distinto.

                Ella enseña doce ajustes aunque la tabla enseñe cuatro, y lo dice en su pie. No
                es un descuido: son dos preguntas distintas -- «¿por dónde voy?» y «¿qué números
                tengo?» -- y él las separó así. */}
            <GraficaDeAjustes entradas={filas} />
            {/* CUÁNTOS AJUSTES ENSEÑA LA TABLA, pegado a la tabla. Estaba arriba, al lado del
                título, y desde que hay gráfica quedaba diciendo «tus cuatro últimos ajustes»
                encima de doce barras. Cada rótulo, al lado de lo que cuenta. */}
            {/* Y SALE TAMBIÉN CON CUATRO JUSTOS (Jesús, 8-09: «abajo falta el texto»).
                La condición era `filas.length > visibles.length`, o sea «solo si hay más de
                cuatro», y con cuatro exactos se callaba: es el caso de su cuenta, que tiene
                cuatro. La condición estaba para que el rótulo no mintiera con tres ajustes,
                pero se pasó de frenada y tapó justo el caso en que sí es verdad.
                Con menos de cuatro se dice sin número, que es lo único que no miente. */}
            {!completo && visibles.length > 0 && (
                <p className="text-[11px] text-muted-foreground" data-testid="mis-macros-cuantos">
                    {visibles.length >= AJUSTES_A_LA_VISTA
                        ? 'Tus cuatro últimos ajustes'
                        : 'Tus últimos ajustes'}
                </p>
            )}
            {/* CON LA SEÑAL DE QUE HAY MÁS A LOS LADOS (punto 51). La columna fija ya
                estaba; lo que faltaba era decir que la tabla sigue. */}
            <TablaDeslizable className="surface p-2" fondo="from-card">
                <table className="w-full text-sm min-w-[520px]">
                    <thead>
                        <tr className="text-muted-foreground text-[10px] uppercase tracking-wider border-b border-border">
                            {/* La fecha se queda fija al desplazar (revisión del 2-09): en
                                móvil la tabla se va de lado y la fecha quedaba cortada en
                                «6», «4», «23», así que no se sabía de qué ajuste era la
                                fila que se estaba mirando. */}
                            <th rowSpan={2} className="px-2 py-1.5 text-left font-semibold sticky left-0 z-10 bg-card">Fecha</th>
                            <th colSpan={3} className="px-1.5 py-1.5 text-center font-bold border-l border-border">Entreno</th>
                            <th colSpan={2} className="px-1.5 py-1.5 text-center font-bold border-l border-border">Peri</th>
                            <th colSpan={3} className="px-1.5 py-1.5 text-center font-bold border-l border-border">Descanso</th>
                            {/* EL PESO, AL FINAL Y AL LADO DE LOS MACROS (Jesús, 7-09, reglas
                                01·8 y 03·1: «fecha, entreno, peri, descanso y peso»). Iba en la
                                segunda columna, entre la fecha y los ocho números, y partía en
                                dos lo que se lee de un tirón: los macros de ese ajuste. */}
                            {/* ENTRE LOS MACROS Y EL PESO, que es donde la dibuja. */}
                            <th rowSpan={2} className="px-2 py-1.5 text-left font-semibold border-l border-border">Objetivo</th>
                            <th rowSpan={2} className="px-2 py-1.5 text-right font-semibold border-l border-border">Peso</th>
                        </tr>
                        <tr className="text-muted-foreground/70 text-[10px] uppercase border-b border-border">
                            <th className="px-1.5 pb-1.5 text-right font-medium border-l border-border">P</th><th className="px-1.5 pb-1.5 text-right font-medium">H</th><th className="px-1.5 pb-1.5 text-right font-medium">G</th>
                            <th className="px-1.5 pb-1.5 text-right font-medium border-l border-border">P</th><th className="px-1.5 pb-1.5 text-right font-medium">H</th>
                            <th className="px-1.5 pb-1.5 text-right font-medium border-l border-border">P</th><th className="px-1.5 pb-1.5 text-right font-medium">H</th><th className="px-1.5 pb-1.5 text-right font-medium">G</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibles.map((h, i) => {
                            // El ajuste anterior EN EL TIEMPO: las filas vienen de la más
                            // nueva a la más vieja, así que es la de debajo del array entero.
                            const previo = filas[i + 1];
                            const clasePeso = h.peso === extremos.min ? 'text-ok'
                                : h.peso === extremos.max ? 'text-red-600 dark:text-red-400' : 'text-foreground';
                            return (
                                <tr key={h.id || i} className="border-b border-border last:border-0">
                                    {/* SU FILA, MARCADA COMO «TÚ» (Jesús, 7-09, regla 07·6):
                                        «así su historia es de verdad su historia». El dato ya
                                        lo trae el servidor (`del_cliente`, del `origen` del
                                        ajuste) y es el mismo con el que el panel del entrenador
                                        escribe «lo cambió el cliente desde su calculadora». */}
                                    <td className="px-2 py-2 whitespace-nowrap font-data text-foreground sticky left-0 z-10 bg-card">
                                        {fechaCorta(h.fecha)}
                                        {h.del_cliente && (
                                            <span className="ml-1.5 text-[10px] font-sans font-bold uppercase tracking-wide text-brand"
                                                title="Este ajuste lo hiciste tú">tú</span>
                                        )}
                                    </td>
                                    <Celdas macros={h.entreno} anterior={previo?.entreno} />
                                    <Celdas macros={h.peri} anterior={previo?.peri} conGrasa={false} />
                                    <Celdas macros={h.descanso} anterior={previo?.descanso} />
                                    {/* Con coma y a la décima, la regla de la casa: salía en
                                        crudo y se leía «78.6 kg» en una app en español. */}
                                    <td className="px-2 py-2 border-l border-border">
                                        <Objetivo clave={h.objetivo} />
                                    </td>
                                    <td className={`px-2 py-2 text-right whitespace-nowrap font-data border-l border-border ${clasePeso}`}>
                                        {h.peso != null ? `${num1(h.peso)} kg` : '-'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </TablaDeslizable>
            {/* EL PIE DICE LAS DOS MARCAS (Jesús, 7-09, pie de la maqueta 03). Antes solo
                anunciaba el rojo, y desde hoy también se marca el peso. */}
            <p className="text-[11px] text-muted-foreground">
                En rojo lo que cambió.{extremos.min != null && ' El peso más bajo y el más alto, marcados.'}
            </p>
            {/* «VER HISTÓRICO COMPLETO», Y LLEVA A OTRA PANTALLA (regla 01·11). Desplegaba
                aquí mismo las 264 filas del que más tiene, que es lo que él llama «un archivo
                en la portada». En la pantalla completa este botón no se pinta. */}
            {/* Y EL ENLACE SALE SIEMPRE QUE HAYA ALGO QUE VER. Colgaba de la misma
                condición que el rótulo, así que al que tiene cuatro ajustes o menos se le
                escondía la única puerta a «Todo tu histórico»: esa pantalla existe y no
                había forma de llegar a ella. Con la tabla enseñando lo mismo que hay, el
                enlace no promete de más: lleva a la pantalla donde además se compara. */}
            {!completo && filas.length > 0 && (
                <Link to="/dashboard/macro-calculator/historico"
                    className="inline-block text-xs font-semibold text-brand hover:underline"
                    data-testid="mis-macros-ver-todo">
                    Ver histórico completo ›
                </Link>
            )}
        </section>
    );
};

export default HistorialDeMacros;
